import { gql } from '@apollo/client';

const ACCOUNT_FIELDS_FRAGMENT = gql`
  fragment AccountFields on Account {
    id
    bin
    isRewards
    activationEligibilityV2 {
      eligible
      reasons {
        description
        reasonCode
      }
    }
    accountDetails {
      creditorDisplay
      accountTag
      debtSaleInfo {
        dateSold
        status
        debtBuyerInfo {
          address
          buyerName
          contactNumber
          email
          website
        }
      }
    }
    archiveState
    statuses {
      isAccountOnWatch
      isBankrupt
      isChargedOff
      isCreditRevoked
      isCardOnWatch
      isClosed
      isDeceased
      isDelinquent
      isInLitigation
      isMilitaryLending
      isOverlimit
      isPastDue
      isReturnMail
      isScra
      daysPastDue
      hasAppFraud
      pastDueStatus
      raw {
        description
        reasonCode
        statusCode
        startDate
        stopDate
        date
      }
    }
    paymentInfo {
      minimumDue
      nextStatementCloseDate
      lastStatementCloseDate
      printDueDate
    }
    balanceInfo {
      availableCredit
      cashAdvanceAvailableCreditLimit
      currentBalance
      pastDueBalance
      statementBalance
      remainingStatementBalance
    }
    cardDetails {
      id: accountId
      bank
      expirationDate
      last4
      lastCardRequestDate
      name
      issuanceState
      cardReceiptVerificationDate
      productDisplayName {
        productCode
        productName
      }
    }
    cardShipping {
      events {
        courier
        date
        trackingNumber
      }
      mostRecentEvent {
        courier
        date
        trackingNumber
      }
    }
    changeInTerms {
      id
      cagUrl
      events {
        name
        timestamp
      }
      noticeName
      newDisclosureGroup
      targetDate
      status
      statusTimestamp
    }
    dueDate {
      changeEligibilityChecklist {
        isEligible
        hasNoChangeInPastSixMonths
        hasTwoOrLessDueDateChanges
        hasNoUpcomingCit
        isPastFirstCycle
        isNotBankrupt
        isNotDelinquent
        isNotChargedOff
        hasPassedFraudValidation
        isNotWithinIntroAprPeriod
      }
    }
    pastDue {
      consecutiveDaysPastDue
      daysPastDue
      pastDueAmountTotal
      pastDueTotal
      pastDues {
        interval
        times
        total
      }
    }
    securedCardInfo {
      currentAmount
      status
      refundedDate
      isRefunded
      originalAmount
    }
    insuranceStatusInfo {
      policyInfo {
        dateEnrolled
        dateStatusEffective
      }
      insuranceType
      status
    }
    insuranceEligibilityInfo {
      insuranceType
      eligible
    }
    digitalWallet {
      id: accountId
      info {
        status
      }
    }
  }
`;

const GET_ACCOUNT_CARD_NUMBER = gql`
  query GetAccountCardNumber(
    $accountId: String!
    $customerId: String!
    $phone: String!
  ) {
    account(accountId: $accountId, customerId: $customerId) {
      id
      tokenizedCardNumber(phone: $phone)
    }
  }
`;

const GET_ACCOUNT_REWARDS = gql`
  query GetAccountRewards($accountId: String!, $customerId: String!) {
    account(accountId: $accountId, customerId: $customerId) {
      id
      rewardsV2 {
        rewardsConfig {
          categoryBonus
          esbAmount
          esbRewardDate
          esbNetSpend
          rewardsRate
          categoryCode
          categoryName
          categoryRewardsRate
        }
        rewardsPayable {
          amount
          twelveMonthRewardsEarned
        }
      }
    }
  }
`;

const ACCOUNT_STATUSES = gql`
  query GetAccountStatuses($customerId: String!, $accountIds: [String!]!) {
    accountFraudStatuses(customerId: $customerId, accountIds: $accountIds) {
      accountId
      accountStatuses {
        description
        reasonCode
        statusCode
      }
    }
  }
`;

const ACCOUNT_STATUS_BALANCE = gql`
  ${ACCOUNT_FIELDS_FRAGMENT}
  query GetAccountStatus($accountId: String!, $customerId: String!) {
    account(accountId: $accountId, customerId: $customerId) {
      ...AccountFields
    }
  }
`;

const ACCOUNT_PRICING_DETAILS = gql`
  query GetAccountPricingDetails($accountId: String!, $customerId: String!) {
    account(accountId: $accountId, customerId: $customerId) {
      id
      pricingDetails {
        annualFee
        cashAdvanceApr
        cashAdvanceCreditLimit
        cashAprType
        creditLimit
        futureApr
        futureAprStartDate
        hasLateFeeGrace
        hasResidualInterest
        introAprLengthInMonths
        monthlyFee
        nextMembershipFeeDate
        previousMembershipFeeDate
        purchaseApr
        purchaseAprType
      }
    }
  }
`;

const ACCOUNT_REFERRALS = gql`
  query GetAccountReferrals($customerId: String!, $accountId: String!) {
    account(customerId: $customerId, accountId: $accountId) {
      id
      offers {
        referral {
          offerId
          data {
            referenceId
            offerId
            offerState
            type
            referenceId
            expirationDate
            invitationCode
            maximumStatementCredit
            statementCreditPerReferral
            referredCustomers {
              amountStatementCredit
              referredDateTime
            }
          }
        }
      }
    }
  }
`;

const GET_UPCOMING_PAYMENT_ACTIVITY = gql`
  query GetUpcomingPaymentActivity($accountId: String!, $customerId: String!) {
    account(accountId: $accountId, customerId: $customerId) {
      id
      upcomingPayments {
        id
        state
      }
      autopay {
        id
        enrolledDate
        scheduleStatus
      }
    }
  }
`;

const GET_ACCOUNT_CARD_LAST_4 = gql`
  query GetAccountCardLast4($accountId: String!, $customerId: String!) {
    account(accountId: $accountId, customerId: $customerId) {
      id
      cardDetails {
        id: accountId
        last4
      }
    }
  }
`;

const GET_ACCOUNT_STATEMENT_PREFERENCES = gql`
  query GetAccountStatementPreferences(
    $accountId: String!
    $customerId: String!
  ) {
    account(accountId: $accountId, customerId: $customerId) {
      id
      statementPreferences {
        canUpdateStatementPreference
        isPaperlessStatement
      }
    }
  }
`;

const GET_ACCOUNT_TRANSFER_HISTORY = gql`
  query GetAccountTransferHistory($accountId: String!, $previousMonths: Int!) {
    accountTransferHistory(
      accountId: $accountId
      previousMonths: $previousMonths
    ) {
      transfers {
        date
        fromCardNumberRedacted
        eventType
        statusReason
        toCardNumberRedacted
      }
    }
  }
`;

const ACCOUNT_STATUSES_RAW = gql`
  query GetAccountStatusesRaw($customerId: String!, $accountId: String!) {
    account(accountId: $accountId, customerId: $customerId) {
      id
      statusesRaw {
        statuses {
          date
          description
          reasonCode
          startDate
          statusCode
          stopDate
        }
      }
    }
  }
`;

const ACCOUNT_BIN = gql`
  query GetAccountBin($accountId: String!, $customerId: String!) {
    account(accountId: $accountId, customerId: $customerId) {
      id
      bin
    }
  }
`;

const GET_AUTOPAY_STATUS = gql`
  query GetAutopayStatus($accountId: String!, $customerId: String!) {
    account(accountId: $accountId, customerId: $customerId) {
      id
      autopay {
        id
        enrolledDate
        scheduleStatus
      }
    }
  }
`;

export {
  ACCOUNT_BIN,
  ACCOUNT_FIELDS_FRAGMENT,
  ACCOUNT_PRICING_DETAILS,
  ACCOUNT_REFERRALS,
  ACCOUNT_STATUS_BALANCE,
  ACCOUNT_STATUSES,
  ACCOUNT_STATUSES_RAW,
  GET_ACCOUNT_CARD_LAST_4,
  GET_ACCOUNT_CARD_NUMBER,
  GET_ACCOUNT_REWARDS,
  GET_AUTOPAY_STATUS,
  GET_UPCOMING_PAYMENT_ACTIVITY,
  GET_ACCOUNT_STATEMENT_PREFERENCES,
  GET_ACCOUNT_TRANSFER_HISTORY,
};
